import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FaTv,
  FaLandmark,
  FaUmbrellaBeach,
  FaBuilding,
  FaHospital,
  FaIndustry,
  FaCar,
  FaShoppingBag,
  FaBriefcase,
  FaUsers,
  FaCog,
} from "react-icons/fa";
import "./ClientsSlider.css";

const ClientsSlider = () => {
  const { i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const clientsEn = [
    "Al Mayadeen Channel",
    "Union Of Municipality Of El Dahyeh El Janoubiyeh",
    "Mleeta Touristic Center",
    "El Ghobeiry Municipality",
    "Borj el Barajneh Municipality",
    "Union Of Municipality Of West Baalbeck",
    "Chmestar Municipality",
    "Union Of Municipality Of Eklim El Toufah",
    "Jbaa Municipality",
    "Beit Shama Municipality",
    "Ste. Scout Al Mahdy",
    "Ste. Al El Beit",
    "Doctors of Al Rassoul Hospital",
    "Doctors of Al Sahel Hospital",
    "Doctors of Al Zahraa Hospital",
    "Doctors of Saint-George Hospital",
    "Al Tourath company for Engineering",
    "Ste. Metal For Contracting & Trading",
    "Techno Motors Company",
    "Ste. Khalifeh For Livestock Trade",
    "Bloom Stores",
    "PRIMO INTERNATIONAL S.A.R.L",
  ];

  const clientsAr = [
    "قناة الميادين",
    "اتحاد بلديات الضاحية الجنوبية",
    "معلم مليتا الجهادي",
    "بلدية الغبيري",
    "بلدية برج البراجنة",
    "اتحاد بلديات غربي بعلبك",
    "بلدية شمسطار",
    "اتحاد بلديات اقليم التفاح",
    "بلدية جباع",
    "بلدية بيت شاما",
    "جمعية كشافة المهدي",
    "جمعية آل البيت",
    "أطباء مستشفى الرسول الأعظم",
    "أطباء مستشفى الساحل",
    "أطباء مستشفى الزهراء",
    "أطباء مستشفى السان جورج",
    "شركة التراث للهندسة",
    "شركة تكنو موتورز",
    "شركة خليفة لتجارة المواشي",
  ];

  // Icon mapping based on company type
  const getClientIcon = (clientName, index) => {
    const name = clientName.toLowerCase();
    
    // Media/TV
    if (name.includes("channel") || name.includes("ميادين")) {
      return FaTv;
    }
    // Tourism
    if (name.includes("touristic") || name.includes("مليتا")) {
      return FaUmbrellaBeach;
    }
    // Municipalities & Unions
    if (name.includes("municipality") || name.includes("union") || name.includes("بلدية") || name.includes("اتحاد")) {
      return FaLandmark;
    }
    // Hospitals
    if (name.includes("hospital") || name.includes("doctors") || name.includes("مستشفى") || name.includes("أطباء")) {
      return FaHospital;
    }
    // Engineering/Construction
    if (name.includes("engineering") || name.includes("contracting") || name.includes("الهندسة") || name.includes("تكنو موتورز")) {
      return FaIndustry;
    }
    // Motors/Automotive
    if (name.includes("motor") || name.includes("موتورز")) {
      return FaCar;
    }
    // Stores/Retail
    if (name.includes("store") || name.includes("bloom")) {
      return FaShoppingBag;
    }
    // Associations/Societies
    if (name.includes("scout") || name.includes("ste.") || name.includes("جمعية")) {
      return FaUsers;
    }
    // Companies/International
    if (name.includes("company") || name.includes("s.a.r.l") || name.includes("international") || name.includes("شركة")) {
      return FaBriefcase;
    }
    // Livestock
    if (name.includes("livestock") || name.includes("مواشي")) {
      return FaCog;
    }
    
    // Default icon
    return FaBuilding;
  };

  const clients = i18n.language === "ar" ? clientsAr : clientsEn;
  const itemsPerSlide = 4;

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.ceil(clients.length / itemsPerSlide));
    }, 3000);

    return () => clearInterval(interval);
  }, [clients.length, isAutoPlaying]);

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev === 0 ? Math.ceil(clients.length / itemsPerSlide) - 1 : prev - 1));
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % Math.ceil(clients.length / itemsPerSlide));
  };

  useEffect(() => {
    setCurrentIndex(0);
  }, [i18n.language]);

  return (
    <div className="clients-slider-container" dir="ltr">
      <div className="clients-slider-wrapper">
        <button className="clients-slider-nav clients-slider-nav--prev" onClick={handlePrev} aria-label="Previous">
          ←
        </button>
        
        <div className="clients-slider">
          <div className="clients-slider-track" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
            {Array.from({ length: Math.ceil(clients.length / itemsPerSlide) }).map((_, slideIndex) => {
              const slideClients = clients.slice(slideIndex * itemsPerSlide, (slideIndex + 1) * itemsPerSlide);
              return (
                <div key={slideIndex} className="clients-slide">
                  {slideClients.map((client, index) => {
                    const globalIndex = slideIndex * itemsPerSlide + index;
                    const IconComponent = getClientIcon(client, globalIndex);
                    return (
                      <div key={index} className="client-item">
                        <div className="client-item-inner">
                          <div className="client-icon-wrapper">
                            <IconComponent className="client-icon" />
                          </div>
                          <span className="client-name">{client}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <button className="clients-slider-nav clients-slider-nav--next" onClick={handleNext} aria-label="Next">
          →
        </button>
      </div>

      <div className="clients-slider-dots">
        {Array.from({ length: Math.ceil(clients.length / itemsPerSlide) }).map((_, index) => (
          <button
            key={index}
            className={`clients-dot ${currentIndex === index ? "active" : ""}`}
            onClick={() => {
              setIsAutoPlaying(false);
              setCurrentIndex(index);
            }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ClientsSlider;

